/**
 * Where the numbers came from, in one line.
 *
 * It has to say whether the portfolio is live or seeded — a reader who cannot tell will quote an
 * assumed cluster FTE as though it were payroll. But it must not push the figures below the fold:
 * the earlier version stacked a warning block per data problem and pushed "Where we stand" off the
 * first screen, which inverted the tab's whole purpose. So the notices collapse to a count, and the
 * detail is one click away.
 */
import { useState } from 'react';
import { Badge } from '@finn-auto/design-system/atoms/badge';
import { ChevronDown, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import type { LivePortfolio } from '../../../types/rolePrioritisation';
import type { ReconcileNote } from '../../../utils/rolePrioritisationState';

interface Notice {
  label: string;
  detail: string;
  items: string[];
}

export function PortfolioSource({
  live,
  loading,
  onRefresh,
  reconcileNotes,
  teamSource,
}: {
  live: LivePortfolio | null;
  loading: boolean;
  onRefresh: () => void;
  reconcileNotes: ReconcileNote[];
  /** Whether the capacity half came from the shared roster or is still the seeded fallback. */
  teamSource: 'shared' | 'seed';
}) {
  const [open, setOpen] = useState(false);
  const isLive = live?.source === 'airtable';
  const notices = buildNotices(live, reconcileNotes, isLive);

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow-card">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2 text-xs text-iron">
        {loading && !live ? (
          <span className="flex items-center gap-1.5">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            reading Airtable…
          </span>
        ) : isLive ? (
          <>
            <Badge variant="green">live from Airtable</Badge>
            <span className="tabular-nums">
              <b className="font-semibold text-black">{live.openRoles}</b> open roles
            </span>
            <span className="text-steel">synced {formatSynced(live.syncedAt)}</span>
          </>
        ) : (
          <>
            <Badge variant="orange">seeded baseline</Badge>
            <span>{live?.reason || 'Airtable could not be read'}</span>
          </>
        )}

        {/*
          ⚠️ THE CAPACITY HALF HAS A SOURCE TOO, and this line is the only thing that says which.
          It used to read "working models assumed" unconditionally, which was written before the
          shared roster existed and has been wrong for everyone who has saved one — while still
          being the only warning for anyone who has not. Every figure on the tab is demand ÷
          capacity; a reader who cannot tell a saved roster from a seeded one will quote an assumed
          FTE as though it were payroll.
        */}
        <span className="text-steel">
          ·{' '}
          {teamSource === 'shared'
            ? 'team from the shared roster'
            : 'team is the seeded baseline — set it on Planning → TA capacity planning'}
        </span>

        {notices.length ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="flex items-center gap-1 rounded-md px-1.5 py-0.5 font-medium text-trustedBlue hover:bg-lightBlue"
          >
            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            {noticeSummary(notices)}
          </button>
        ) : null}

        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="ml-auto flex h-7 items-center gap-1.5 rounded-md border border-solid border-pewter bg-white px-2.5 text-[13px] font-medium text-black hover:bg-cotton disabled:opacity-50"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {open && notices.length ? (
        <div className="border-t border-solid border-pearl bg-cotton px-4 py-3 text-xs text-asphalt">
          {notices.map((n) => (
            <div key={n.label} className="mb-3 last:mb-0">
              <div className="text-[13px] font-semibold text-black">{n.label}</div>
              <div className="mt-0.5 max-w-[92ch] leading-4 text-iron">{n.detail}</div>
              <ul className="mt-1 list-disc pl-4 leading-5">
                {n.items.slice(0, 6).map((t) => (
                  <li key={t}>{t}</li>
                ))}
                {n.items.length > 6 ? <li>…and {n.items.length - 6} more</li> : null}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** "2 data notes" — one number, so the strip stays one line however many there are. */
function noticeSummary(notices: Notice[]): string {
  const n = notices.reduce((s, x) => s + x.items.length, 0);
  return `${n} data ${n === 1 ? 'note' : 'notes'}`;
}

function buildNotices(
  live: LivePortfolio | null,
  reconcileNotes: ReconcileNote[],
  isLive: boolean
): Notice[] {
  const out: Notice[] = [];
  const unrated = isLive ? (live?.unrated ?? []) : [];
  const unattributed = isLive ? (live?.hygiene?.unattributed ?? []) : [];
  const crossCluster = isLive ? (live?.hygiene?.crossCluster ?? []) : [];

  if (unrated.length) {
    out.push({
      label: `${unrated.length} open ${unrated.length === 1 ? 'role has' : 'roles have'} no priority rating`,
      detail:
        'Counted as open work, priced at zero — so the demand figure is a floor, not a reading. Rate them in Airtable.',
      items: unrated.map((r) => `${r.title}${r.recruiter ? ` · ${r.recruiter}` : ''}`),
    });
  }
  if (unattributed.length) {
    out.push({
      label: `${unattributed.length} open ${unattributed.length === 1 ? 'role is' : 'roles are'} in no cluster`,
      detail:
        'Neither People Team nor the recruiter map places them, so their hours appear in no cluster’s load. Set People Team in Airtable, or add the recruiter to taCheckinConfig.js — the code will not guess.',
      items: unattributed.map((r) => `${r.title}${r.recruiter ? ` · ${r.recruiter}` : ''}`),
    });
  }
  const fallback = isLive ? (live?.hygiene?.byRecruiterFallback ?? []) : [];
  if (fallback.length) {
    out.push({
      label: `${fallback.length} ${fallback.length === 1 ? 'role has' : 'roles have'} no People Team set`,
      detail:
        'Placed by the recruiter map instead, so the attribution holds — but People Team is the field that is meant to state it, and the recruiter map has to be maintained by hand. Filling these in is the durable fix.',
      items: fallback.map((r) => `${r.title} · ${r.recruiter} → ${r.cluster}`),
    });
  }
  if (crossCluster.length) {
    out.push({
      label: `${crossCluster.length} ${crossCluster.length === 1 ? 'role sits' : 'roles sit'} outside the unit that owns it`,
      detail:
        'Expected, not an error: capacity follows the cluster that owns the role, which is not always the unit it reports into.',
      items: crossCluster.map((r) => `${r.title} · owned by ${r.carriedBy}, sits in ${r.sitsIn}`),
    });
  }
  if (reconcileNotes.length) {
    out.push({
      label: 'Your local what-ifs were adjusted',
      detail:
        'Support and capacity choices are stored per cluster and per person, so a change to the working models or the cluster list can invalidate them.',
      items: reconcileNotes.map((n) => n.detail),
    });
  }
  return out;
}

function formatSynced(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'just now';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  return hours < 24 ? `${hours} h ago` : `${Math.round(hours / 24)} d ago`;
}
